/**
 * The agent page's Now (wave C, decision 0071, spec §3.2) and the pack's `## Now`: one function for both. Doing now
 * (her live turn and the live runs on items she owns), Tracking (active goals with their latest KPI, milestones in Now
 * and Next), Open loops by whose move, Since her last turn (the wakes she got, from `Run.wakeReasons`; changes to her
 * items that woke nobody, with Tell), and the daily check's line. No wake re-derivation. Pure.
 */
import {
  type AgentNowView,
  NOW_PACK_ROWS,
  NOW_PAGE_ROWS,
  type NowRow,
  type NowSection,
} from "@minga/protocol"
import { kpiPeriodLabel, kpiValueText } from "./kpi-period"
import { looseEnds } from "./loose-ends"
import { dailyCheckHourOf, monitorOf, projectZone } from "./monitors"
import { recipientOf } from "./multiplayer"
import { isRunEnded, itemOf, kpiOf, leadOf } from "./state"
import { isPlanningItem, progressOf } from "./tree"
import type {
  Agent,
  AgentId,
  Comment,
  Item,
  Principal,
  ProjectState,
  Proposal,
  Run,
  WakeReason,
} from "./types"
import { heldLine, isPersonNote } from "./wakes"

/** What the Project DO reads besides state: her recent turn runs, comments on her items, her last turn's end. */
export interface AgentOverviewInput {
  /** Her turn runs, newest first (at least those that ended since `since`, plus the live one). */
  readonly turns: ReadonlyArray<Run>
  /** Comments on items she owns since `since`, oldest first. */
  readonly comments: ReadonlyArray<Comment>
  /** When her last turn ended; null = never. */
  readonly since: number | null
}

const MIN = 60_000
const TEXT_MAX = 300

const cut = (text: string, max = TEXT_MAX): string =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text

const firstLine = (text: string, max = 120): string =>
  cut((text.split("\n").find((l) => l.trim() !== "") ?? "").trim(), max)

const minutes = (ms: number): string => {
  const m = Math.max(0, Math.round(ms / MIN))
  return m < 120 ? `${m} min` : `${Math.round(m / 60)} h`
}

const personName = (s: ProjectState, userId: string): string =>
  s.members.find((m) => m.userId === userId)?.displayName ?? "a person"

const principalName = (s: ProjectState, p: Principal | null | undefined): string | null => {
  if (!p) return null
  if (p._tag === "user") return personName(s, p.userId)
  return s.agents.find((a) => a.id === p.agentId)?.name ?? "an agent"
}

const itemRef = (s: ProjectState, id: string | null | undefined): string =>
  (id ? itemOf(s, id as Item["id"])?.ref : undefined) ?? "an item"

/** One wake in plain words ("Kate decided P-3 for Becky", "W-46 finished DIC-137", "the daily check: 2 loose ends"). */
export const wakeWords = (s: ProjectState, r: WakeReason): string => {
  switch (r._tag) {
    case "user_message":
      return `${personName(s, r.from)} wrote in chat`
    case "human_mention":
      return `${personName(s, r.from)} mentioned the agent`
    case "run_ended": {
      const run = s.runs.find((x) => x.id === r.runId)
      return `${run?.worker?.ref ?? run?.ref ?? "a run"} ${r.outcome === "completed" ? "finished" : `ended (${r.outcome})`}${r.itemId ? ` ${itemRef(s, r.itemId)}` : ""}`
    }
    case "review_requested":
      return `${itemRef(s, r.itemId)} waits for a review`
    case "children_closed":
      return `everything under ${itemRef(s, r.itemId)} is closed`
    case "proposal_resolved": {
      const p = s.proposals.find((x) => x.id === r.proposalId)
      const by = r.by ? personName(s, r.by) : "someone"
      const forWhom = r.for ? ` for ${personName(s, r.for)}` : ""
      return `${by} decided ${p?.decisionRef ?? p?.ref ?? "a card"}${forWhom} (${r.state})`
    }
    case "monitor": {
      const m = monitorOf(s, r.monitorId)
      return m ? `monitor "${firstLine(m.instruction, 80)}"` : "a monitor"
    }
    case "series_due":
      return `${itemRef(s, r.itemId)} is due for ${r.period}`
    case "loose_ends":
      // decision 0102: a card addressed to her rides a `loose_ends` wake (`card_for:<id>`)
      if (r.keys.every((k) => k.startsWith("card_for:")))
        return `${r.keys.length === 1 ? "a card" : `${r.keys.length} cards`} to decide or escalate`
      return `the daily check: ${r.keys.length} loose ${r.keys.length === 1 ? "end" : "ends"}`
    case "kpi_threshold":
      return `${kpiOf(s, r.kpiId)?.name ?? "a KPI"} crossed its threshold`
    case "changed":
      // merge of 0070 × 0071: a person's plain note is quoted only in turn.md (a stranger's taints it); these rows also
      // go into the pack's `## Now`, which carries no taint, so they name the note and leave its text out
      if (isPersonNote(r) && r.by._tag === "user")
        return `${personName(s, r.by.userId)} left a note on ${itemRef(s, r.id)}`
      return heldLine(s, r)
    default:
      return heldLine(s, r)
  }
}

const row = (
  r: Omit<NowRow, "text" | "why"> & { readonly text: string; readonly why: string | null },
): NowRow => ({
  ...r,
  text: cut(r.text),
  why: r.why === null ? null : cut(r.why),
})

const section = (rows: ReadonlyArray<NowRow>, cap = NOW_PAGE_ROWS): NowSection => ({
  rows: rows.slice(0, cap),
  more: Math.max(0, rows.length - cap),
})

const ownsItem = (t: Item, agentId: AgentId): boolean =>
  t.owner?._tag === "agent" && t.owner.agentId === agentId

/** Is this agent the project's lead (her Tracking shows every goal and milestone)? */
const isLead = (s: ProjectState, a: Agent): boolean => leadOf(s)?.id === a.id

const doingNow = (
  s: ProjectState,
  a: Agent,
  now: number,
  input: AgentOverviewInput,
): ReadonlyArray<NowRow> => {
  const out: NowRow[] = []
  const live =
    input.turns.find((r) => r.kind === "turn" && r.agentId === a.id && !isRunEnded(r)) ??
    s.runs.find((r) => r.kind === "turn" && r.agentId === a.id && !isRunEnded(r))
  if (live) {
    const reasons = live.wakeReasons ?? []
    const where =
      live.executor?._tag === "runner"
        ? "on a runner"
        : live.executor?._tag === "platform"
          ? "in Minga"
          : live.state._tag === "waiting"
            ? "waiting for a runner"
            : "starting"
    const asked = live.askedBy?._tag === "user" ? `for ${principalName(s, live.askedBy)}` : null
    out.push(
      row({
        key: `turn:${live.id}`,
        kind: "turn",
        text: [`Thinking ${where}`, asked, minutes(now - (live.startedAt ?? live.requestedAt))]
          .filter((x) => x !== null)
          .join(" · "),
        why:
          reasons.length > 0
            ? `woke for: ${countedWords(reasons.map((r) => wakeWords(s, r))).join("; ")}`
            : "woke for: a turn",
        subject: { _tag: "run", id: live.id },
        ref: live.ref,
        at: live.startedAt ?? live.requestedAt,
        ...(live.askedBy ? { waitingOn: live.askedBy } : {}),
        action: { _tag: "open" },
        ...(live.state._tag === "waiting" && live.state.unmet.length > 0
          ? { waiting: live.state.unmet }
          : {}),
      }),
    )
  }
  for (const r of s.runs) {
    if ((r.kind !== "worker" && r.kind !== "validator") || isRunEnded(r) || !r.itemId) continue
    const t = itemOf(s, r.itemId)
    if (!t || !ownsItem(t, a.id)) continue
    const who = r.worker?.ref ?? r.ref
    const state =
      r.state._tag === "waiting" ? "waiting for a runner" : r.state._tag === "requested" ? "starting" : null
    out.push(
      row({
        key: `run:${r.id}`,
        kind: "run",
        text: [`${who} on ${t.ref}`, state, minutes(now - (r.startedAt ?? r.requestedAt))]
          .filter((x) => x !== null)
          .join(" · "),
        why: `for: ${t.ref} ${t.title}`,
        subject: { _tag: "item", id: t.id },
        ref: t.ref,
        at: r.startedAt ?? r.requestedAt,
        ...(r.askedBy ? { waitingOn: r.askedBy } : {}),
        action: { _tag: "open" },
        ...(r.state._tag === "waiting" && r.state.unmet.length > 0 ? { waiting: r.state.unmet } : {}),
      }),
    )
  }
  return out
}

const tracking = (s: ProjectState, a: Agent): ReadonlyArray<NowRow> => {
  const out: NowRow[] = []
  const lead = isLead(s, a)
  const mineArea = (t: Item) => lead || (a.areaId !== null && t.areaId === a.areaId)
  const areaGoals = new Set(s.items.filter((t) => mineArea(t)).flatMap((t) => t.goalIds))
  for (const g of s.goals) {
    if (g.status !== "active" || !(lead || areaGoals.has(g.id))) continue
    const k = g.target ? kpiOf(s, g.target.kpiId) : undefined
    const number = k
      ? k.latest
        ? `${k.name} ${kpiValueText(k, k.latest.value)} for ${kpiPeriodLabel(k.cadence, k.latest.at)}`
        : `${k.name}: no number yet`
      : "judged by a person"
    out.push(
      row({
        key: `goal:${g.id}`,
        kind: "goal",
        text: `${g.ref} ${g.title} · ${number}`,
        why: g.target ? `target ${g.target.op} ${g.target.value}` : null,
        subject: { _tag: "goal", id: g.id },
        ref: g.ref,
        at: k?.latest?.at ?? null,
        action: { _tag: "open" },
      }),
    )
  }
  for (const t of s.items) {
    if (!isPlanningItem(s, t) || !mineArea(t)) continue
    if (t.status !== "in_progress" && t.status !== "todo") continue
    const p = progressOf(s, t)
    out.push(
      row({
        key: `milestone:${t.id}`,
        kind: "milestone",
        text: `${t.ref} ${t.title} · ${t.status === "in_progress" ? "Now" : "Next"} · ${p.done} of ${p.total} done`,
        why: null,
        subject: { _tag: "item", id: t.id },
        ref: t.ref,
        at: t.updatedAt,
        action: { _tag: "open" },
      }),
    )
  }
  return out
}

/** A card's line and whose move it is. */
const cardRow = (s: ProjectState, p: Proposal, mine: boolean, agentId: AgentId): NowRow => {
  const to = recipientOf(s, p)
  // decision 0102: her card is with another agent on its way up the chain
  const ap = p.approver
  const holder = !mine && ap._tag === "agent" ? s.agents.find((x) => x.id === ap.agentId) : undefined
  return row({
    key: `card:${p.id}`,
    kind:
      p.body._tag === "question" && p.body.escalation && p.body.subject?._tag === "agent" ? "hold" : "card",
    text: `${p.ref} ${p.oneLine}`,
    why: mine
      ? "waits for the agent's decision"
      : holder
        ? `with @${holder.handle}`
        : `waits on ${personName(s, to)}`,
    subject: { _tag: "proposal", id: p.id },
    ref: p.ref,
    at: p.createdAt,
    move: mine ? "her" : "person",
    waitingOn: mine
      ? { _tag: "agent", agentId }
      : holder
        ? { _tag: "agent", agentId: holder.id }
        : { _tag: "user", userId: to },
    action: { _tag: "decide", proposalId: p.id },
  })
}

const openLoops = (s: ProjectState, a: Agent): ReadonlyArray<NowRow> => {
  const out: NowRow[] = []
  for (const p of s.proposals) {
    if (p.state !== "open") continue
    const byHer =
      (p.proposedBy._tag === "agent" || p.proposedBy._tag === "worker") && p.proposedBy.agentId === a.id
    const aboutHer =
      p.body._tag === "question" && p.body.subject?._tag === "agent" && p.body.subject.id === a.id
    // decision 0102: a card addressed to her on its way up the chain is hers to move too
    const forHer = (p.approver._tag === "lead" || p.approver._tag === "agent") && p.approver.agentId === a.id
    if (forHer) out.push(cardRow(s, p, true, a.id))
    else if (byHer || aboutHer) out.push(cardRow(s, p, false, a.id))
  }
  for (const c of s.openAsks) {
    if (c.ask?.state !== "open") continue
    const toHer = c.ask.to === a.id
    const fromHer =
      (c.createdBy._tag === "agent" || c.createdBy._tag === "worker") &&
      c.createdBy.agentId === a.id &&
      !toHer
    if (!toHer && !fromHer) continue
    const target = s.agents.find((x) => x.id === c.ask?.to)
    const text = c.tainted ? "an ask from a tainted run" : `"${firstLine(c.body)}"`
    out.push(
      row({
        key: `ask:${c.id}`,
        kind: "ask",
        text: toHer ? `Asked: ${text}` : `Asked @${target?.handle ?? "an agent"}: ${text}`,
        why: toHer ? "waits for the agent's answer" : `waits on @${target?.handle ?? "an agent"}`,
        subject: c.target,
        ref: null,
        at: c.createdAt,
        move: toHer ? "her" : "person",
        waitingOn: { _tag: "agent", agentId: c.ask.to },
        action: toHer ? { _tag: "answer", askId: c.id } : { _tag: "open" },
      }),
    )
  }
  for (const art of s.artifacts) {
    const snap = art.pr?.snapshot
    if (snap?.state !== "open" || !art.itemId) continue
    const t = itemOf(s, art.itemId)
    if (!t || !ownsItem(t, a.id)) continue
    const failing = snap.checks.state === "failure" || snap.checks.state === "error"
    const state = failing
      ? "checks failing"
      : snap.mergeable === "conflicting"
        ? "conflicts"
        : snap.reviewDecision === "changes_requested"
          ? "changes requested"
          : snap.reviewDecision === "approved"
            ? "approved, not merged"
            : "waiting for review"
    const herMove = failing || snap.mergeable === "conflicting" || snap.reviewDecision === "changes_requested"
    out.push(
      row({
        key: `pr:${art.id}`,
        kind: "pr",
        text: `PR #${snap.number} on ${t.ref} · ${state}`,
        why: herMove ? "needs a fix" : "waits on a review or a merge",
        subject: { _tag: "artifact", id: art.id },
        ref: t.ref,
        at: art.createdAt,
        move: herMove ? "her" : "person",
        ...(herMove ? {} : { waitingOn: t.askedBy ?? null }),
        action: { _tag: "open" },
      }),
    )
  }
  for (const t of s.items) {
    if (t.status !== "blocked" || !ownsItem(t, a.id)) continue
    const b = t.blocked
    const what =
      b?._tag === "reported"
        ? b.blocker._tag === "depends_on"
          ? `waits on ${itemRef(s, b.blocker.itemId)}`
          : firstLine(b.blocker.what, 120)
        : b?._tag === "needs_human"
          ? "waits on a person's card"
          : b?._tag === "question"
            ? "waits on an answer"
            : "claimed impossible"
    out.push(
      row({
        key: `stuck:${t.id}`,
        kind: "stuck",
        text: `${t.ref} ${t.title}`,
        why: `blocked: ${what}`,
        subject: { _tag: "item", id: t.id },
        ref: t.ref,
        at: t.updatedAt,
        move: "stuck",
        action: { _tag: "open" },
      }),
    )
  }
  // whose move: a person's first, then hers, then stuck; oldest first within
  const order = { person: 0, her: 1, stuck: 2 } as const
  return out.sort((x, y) => order[x.move ?? "stuck"] - order[y.move ?? "stuck"] || (x.at ?? 0) - (y.at ?? 0))
}

/**
 * Wake words said once each, in first-seen order, a repeat counted: ten fires of one monitor are
 * `the monitor "Check the reviews" · 10×`, not ten rows (Now's Since, the live turn's "woke for", History).
 */
export const countedWords = (words: ReadonlyArray<string>): ReadonlyArray<string> => {
  const counts = new Map<string, number>()
  for (const w of words) counts.set(w, (counts.get(w) ?? 0) + 1)
  return [...counts].map(([w, n]) => (n > 1 ? `${w} · ${n}×` : w))
}

const since = (s: ProjectState, a: Agent, input: AgentOverviewInput): ReadonlyArray<NowRow> => {
  const out: NowRow[] = []
  // (1) the wakes of her newest turn (the live one, else the last one), one row per distinct wake (the row's kind
  // already says it woke her)
  const turn = input.turns.find((r) => r.kind === "turn" && r.agentId === a.id)
  countedWords((turn?.wakeReasons ?? []).map((r) => wakeWords(s, r))).forEach((text, i) => {
    out.push(
      row({
        key: `woke:${turn?.id}:${i}`,
        kind: "woke",
        text,
        why: null,
        subject: turn ? { _tag: "run", id: turn.id } : null,
        ref: turn?.ref ?? null,
        at: turn?.requestedAt ?? null,
        action: null,
      }),
    )
  })
  // (2) changes to her items that woke nobody: comments whose wakes don't name her (an agent's note without an
  // @mention); a person's comment already wakes the owner (decision 0035)
  for (const c of input.comments) {
    if (c.target._tag !== "item" || c.kind === "system") continue
    if (input.since !== null && c.createdAt <= input.since) continue
    const t = itemOf(s, c.target.id)
    if (!t || !ownsItem(t, a.id)) continue
    if (c.wakes.some((w) => w.agentId === a.id)) continue
    if ((c.createdBy._tag === "agent" || c.createdBy._tag === "worker") && c.createdBy.agentId === a.id)
      continue
    const by =
      c.createdBy._tag === "user"
        ? personName(s, c.createdBy.userId)
        : c.createdBy._tag === "agent"
          ? (s.agents.find((x) => x.id === (c.createdBy as { agentId: AgentId }).agentId)?.name ?? "an agent")
          : "a worker"
    out.push(
      row({
        key: `quiet:${c.id}`,
        kind: "quiet",
        // a person's words stay out for the same reason as a person's note in `wakeWords`
        text: `${by} commented on ${t.ref}${c.tainted || c.createdBy._tag === "user" ? "" : `: "${firstLine(c.body, 100)}"`}`,
        why: "this woke nobody",
        subject: { _tag: "item", id: t.id },
        ref: t.ref,
        at: c.createdAt,
        action: { _tag: "tell", itemId: t.id },
      }),
    )
  }
  return out
}

const dailyCheckLine = (s: ProjectState, a: Agent, now: number): AgentNowView["dailyCheck"] => {
  const dc = s.dailyCheck
  const keys = new Set(dc?.keys ?? [])
  const lead = leadOf(s)?.id
  const found = dc
    ? looseEnds(s, now).filter((l) => keys.has(l.key) && (l.agentId ?? lead) === a.id).length
    : 0
  return {
    hour: dailyCheckHourOf(s),
    tz: projectZone(s),
    lastAt: dc?.lastAt ?? null,
    found,
    wokeAt: dc?.wokeAt ?? null,
  }
}

export const agentOverview = (
  s: ProjectState,
  agentId: AgentId,
  now: number,
  input: AgentOverviewInput,
): AgentNowView => {
  const a = s.agents.find((x) => x.id === agentId)
  if (!a)
    return {
      agentId,
      at: now,
      doingNow: section([]),
      tracking: section([]),
      openLoops: section([]),
      since: { from: input.since, rows: [], more: 0 },
      dailyCheck: null,
    }
  const sinceRows = since(s, a, input)
  return {
    agentId,
    at: now,
    doingNow: section(doingNow(s, a, now, input)),
    tracking: section(tracking(s, a)),
    openLoops: section(openLoops(s, a)),
    since: {
      from: input.since,
      rows: sinceRows.slice(0, NOW_PAGE_ROWS),
      more: Math.max(0, sinceRows.length - NOW_PAGE_ROWS),
    },
    dailyCheck: dailyCheckLine(s, a, now),
  }
}

/**
 * The pack's `## Now`: at most `cap` rows in total, in this order: waiting on a person, stuck, doing now, her move,
 * tracking, since. `more`: rows left out ("and 12 more: `read_graph`"), the sections' own `more` included.
 */
export const nowRowsForPack = (
  view: AgentNowView,
  cap: number = NOW_PACK_ROWS,
): { readonly rows: ReadonlyArray<NowRow>; readonly more: number } => {
  const loops = view.openLoops.rows
  const ordered = [
    ...loops.filter((r) => r.move === "person"),
    ...loops.filter((r) => r.move === "stuck"),
    ...view.doingNow.rows,
    ...loops.filter((r) => r.move === "her"),
    ...view.tracking.rows,
    ...view.since.rows,
  ]
  const more =
    Math.max(0, ordered.length - cap) +
    view.openLoops.more +
    view.doingNow.more +
    view.tracking.more +
    view.since.more
  return { rows: ordered.slice(0, cap), more }
}

/** `## Now` as the turn file writes it (spec §3.2): one line per row with its why, then "and n more: `read_graph`". */
export const nowPackLines = (view: AgentNowView, cap: number = NOW_PACK_ROWS): ReadonlyArray<string> => {
  const { rows, more } = nowRowsForPack(view, cap)
  if (rows.length === 0) return ["## Now", "", "Nothing is moving.", ""]
  return [
    "## Now",
    "",
    ...rows.map((r) => `- ${r.text}${r.why ? ` (${r.why})` : ""}`),
    ...(more > 0 ? [`- and ${more} more: \`read_graph\``] : []),
    "",
  ]
}
